import { Polygon, TypeMask } from '@shared/services';

/**
 * Enum representing the status of a tire groove depth task.
 * @enum {string}
 * @property {string} CREATED - Task has been created.
 * @property {string} QUEUED - Task is queued for processing.
 * @property {string} PROCESSING - Task is currently being processed.
 * @property {string} FINISHED - Task has finished processing.
 * @property {string} FAILED - Task processing failed.
 * @property {string} VALIDATION_FAILED - Task failed validation.
 */
export enum TireGrooveDepthTaskStatus {
  CREATED = 'created',
  QUEUED = 'queued',
  PROCESSING = 'processing',
  FINISHED = 'finished',
  FAILED = 'failed',
  VALIDATION_FAILED = 'validation_failed',
}

/**
 * Enum representing the location of a tire groove.
 * @enum {string}
 * @property {string} INNER - Inner groove.
 * @property {string} MIDDLE - Middle groove.
 * @property {string} OUTER - Outer groove.
 */
export enum TireGrooveLocation {
  INNER = 'inner',
  MIDDLE = 'middle',
  OUTER = 'outer',
}

/**
 * Type representing a unique identifier for a tire groove depth task.
 * Format: tgd_<unique_string>
 */
export type TTireGrooveDepthTaskId = `tgd_${string}`;

/**
 * Interface representing the geometry of a tire groove.
 * @property {Polygon} points - The polygon points defining the groove geometry.
 */
export interface ITireGrooveGeometry {
  points: Polygon;
}
/**
 * Interface for tire groove image validation result.
 * @interface ITireGrooveImageValidation
 * @property {boolean} overall_ok - Indicates if the image passed validation.
 * @property {string[]} reject_reasons - List of reasons for rejection if validation failed.
 */
export interface ITireGrooveImageValidation {
  overall_ok: boolean;
  reject_reasons: string[];
}

/**
 * Represents a segmentation of a tire groove.
 * @interface
 * @property {TireGrooveLocation} location - Location of the groove (inner, middle, outer).
 * @property {number|null} depth - Measured depth of the groove in 32nds of an inch, or null if unavailable.
 * @property {ITireGrooveGeometry} [geometry] - Geometry of the groove.
 * @property {string|null} [rating] - AI wear rating for the zone (e.g. good/worn).
 * @property {number|null} [depthMm] - Measured depth in millimetres.
 * @property {number|null} [confidence] - AI confidence for the measurement (0..1).
 */
export interface ITireGrooveSegmentation {
  location: TireGrooveLocation;
  depth: number | null;
  geometry?: ITireGrooveGeometry;
  rating?: string | null;
  depthMm?: number | null;
  confidence?: number | null;
}

/**
 * Data payload for tire groove depth callback.
 * @interface
 * @property {ITireGrooveImageValidation|null} [image_validation] - Result of image validation, or null if not available.
 * @property {ITireGrooveSegmentation[]} grooves - List of groove segmentations.
 */
export interface ITireGrooveDepthCallbackData {
  image_validation?: ITireGrooveImageValidation | null;
  grooves: ITireGrooveSegmentation[];
}

/**
 * Response structure for tire groove depth callback.
 * @interface
 * @property {TireGrooveDepthTaskStatus} status - Status of the groove depth task.
 * @property {TTireGrooveDepthTaskId} task_id - Unique identifier for the groove depth task.
 * @property {string|null} [description] - Optional description or error message.
 * @property {ITireGrooveDepthCallbackData|null} [data] - Optional callback data payload.
 */
export interface ITireGrooveDepthCallbackResponse {
  status: TireGrooveDepthTaskStatus;
  task_id: TTireGrooveDepthTaskId;
  description?: string | null;
  data?: ITireGrooveDepthCallbackData | null;
}

/**
 * The result of tire groove depth recognition after AI processing.
 * @interface ITireGrooveDepthRecognitionResult
 * @property {string | null} type - The type of the tire groove.
 * @property {string} photoUrl - The URL of the analyzed photo.
 * @property {ITireGrooveSegmentation[]} grooves - The recognized tire grooves.
 */
export interface ITireGrooveDepthRecognitionResult {
  type: string | null;
  photoUrl: string;
  grooves: ITireGrooveSegmentation[];
}

/**
 * Array of type masks relevant for tire groove depth recognition.
 * Includes masks for VIN sticker and all four tire treads.
 * @type {TypeMask[]}
 */
export const TIRE_GROOVE_DEPTH_MASKS: TypeMask[] = [
  TypeMask.EXTERIOR_VIN_STICKER,
  TypeMask.WHEEL_LEFT_FRONT_TIRE_TREAD,
  TypeMask.WHEEL_LEFT_REAR_TIRE_TREAD,
  TypeMask.WHEEL_RIGHT_FRONT_TIRE_TREAD,
  TypeMask.WHEEL_RIGHT_REAR_TIRE_TREAD,
];
